import { t } from '@lingui/macro';
import { isArray, isEmpty, map, sortBy } from 'lodash';
import { i18n } from '@lingui/core';
import { COLUMN_TYPES } from '../../../data-table/DataTableColumns';
import { FORMATTER_TYPES } from '../../../../utilities/FormattingUtilities';
import {
    CB_QOS_CLASSES_FIELD,
    DESCRIPTION_FIELD,
    DEVICE_FIELD,
    DEVICE_IP_ADDRESS_FIELD,
    DEVICE_NAME_FIELD,
    DISPLAY_SPEED_FIELD,
    IF_ADMIN_STATUS_FIELD,
    IF_OP_STATUS_TEXT_FIELD,
    IF_TYPE_FIELD,
    LABEL_COLLECTOR,
    LABEL_DEVICE_IP_ADDRESS,
    LABEL_DEVICE_NAME_NAME,
    LIST_COLLECTORS_ID,
    MONITORED_INFRASTRUCTURE_FUNCTION_NAME,
    NAME_FIELD,
    PLCNAME_FIELD,
    POLLING_STATUS_FIELD,
    PRIORITY_STATUS_FIELD,
} from '../InfrastructureConstants';
import { buildPageTargetWithIdProp } from '../../AdminLinkUtils';
import { COLLECTORS_FUNCTION_NAME } from '../../collectors/CollectorsAdminConstants';
import {
    getPollingStatusTooltip,
    getPriorityStatusTooltip,
    getSpeedTooltip,
    renderPollingStatus,
    renderPriorityStatus,
    renderSpeed,
} from './InterfaceRenderers';

/**
 * Formats cbQosClasses array (objects with .name) to sorted comma-separated string
 * @param {Array<{name: string}>|*} value - Array of QoS class objects or other value
 * @returns {string} Sorted comma-separated class names or '--' when empty
 */
const formatCbQosClasses = (value) => {
    if (!isArray(value) || isEmpty(value)) return '--';
    const names = map(value, NAME_FIELD);
    return sortBy(names, (n) => n?.toLowerCase()).join(', ');
};

/**
 * Common column definitions shared between device and monitored interface tables
 */
const COMMON_COLUMNS = {
    name: {
        type: COLUMN_TYPES.TEXT,
        title: i18n._(t`Name`),
        mapping: NAME_FIELD,
        width: 240,
        sortable: true,
    },
    pollingStatus: {
        type: COLUMN_TYPES.TEXT,
        title: i18n._(t`Polling Status`),
        mapping: POLLING_STATUS_FIELD,
        width: 140,
        sortable: true,
        renderer: renderPollingStatus,
        getTooltip: getPollingStatusTooltip,
    },
    priority: {
        type: COLUMN_TYPES.TEXT,
        title: i18n._(t`Priority`),
        mapping: PRIORITY_STATUS_FIELD,
        width: 100,
        sortable: true,
        renderer: renderPriorityStatus,
        getTooltip: getPriorityStatusTooltip,
    },
    type: {
        type: COLUMN_TYPES.TEXT,
        title: i18n._(t`Type`),
        mapping: IF_TYPE_FIELD,
        sortable: true,
        width: 120,
    },
    adminStatus: {
        type: COLUMN_TYPES.TEXT,
        title: i18n._(t`Admin Status`),
        mapping: IF_ADMIN_STATUS_FIELD,
        sortable: true,
        width: 130,
    },
    opStatus: {
        type: COLUMN_TYPES.TEXT,
        title: i18n._(t`Op Status`),
        mapping: IF_OP_STATUS_TEXT_FIELD,
        sortable: true,
        width: 120,
    },
    speed: {
        type: COLUMN_TYPES.NUMERIC,
        title: i18n._(t`Speed`),
        mapping: DISPLAY_SPEED_FIELD,
        sortProperty: DISPLAY_SPEED_FIELD,
        width: 100,
        sortable: true,
        formatter: FORMATTER_TYPES.LOG,
        renderer: renderSpeed,
        getTooltip: getSpeedTooltip,
    },
    description: {
        type: COLUMN_TYPES.TEXT,
        title: i18n._(t`Description`),
        mapping: DESCRIPTION_FIELD,
        sortable: true,
        flex: 1,
        minWidth: 125,
    },
    classNames: {
        type: COLUMN_TYPES.TEXT,
        title: i18n._(t`Class Names`),
        mapping: CB_QOS_CLASSES_FIELD,
        flex: 1,
        minWidth: 100,
        formatter: formatCbQosClasses,
    },
};

/**
 * Columns specific to monitored interface tables
 */
const MONITORED_SPECIFIC_COLUMNS = {
    deviceName: {
        type: COLUMN_TYPES.DRILLINLINK,
        mapping: DEVICE_NAME_FIELD,
        title: LABEL_DEVICE_NAME_NAME,
        sortable: true,
        width: 235,
        link: {
            target: buildPageTargetWithIdProp(MONITORED_INFRASTRUCTURE_FUNCTION_NAME, DEVICE_FIELD),
        },
    },
    deviceIpAddress: {
        type: COLUMN_TYPES.DRILLINLINK,
        mapping: DEVICE_IP_ADDRESS_FIELD,
        title: LABEL_DEVICE_IP_ADDRESS,
        sortable: true,
        width: 120,
        link: {
            target: buildPageTargetWithIdProp(MONITORED_INFRASTRUCTURE_FUNCTION_NAME, DEVICE_FIELD),
        },
        sortProperty: DEVICE_IP_ADDRESS_FIELD,
    },
    collector: {
        type: COLUMN_TYPES.DRILLINLINK,
        mapping: PLCNAME_FIELD,
        title: LABEL_COLLECTOR,
        sortable: true,
        width: 225,
        link: {
            target: buildPageTargetWithIdProp(COLLECTORS_FUNCTION_NAME, LIST_COLLECTORS_ID),
        },
    },
};

export const DEVICE_INTERFACE_COLUMNS = [
    COMMON_COLUMNS.name,
    COMMON_COLUMNS.pollingStatus,
    COMMON_COLUMNS.priority,
    COMMON_COLUMNS.type,
    COMMON_COLUMNS.adminStatus,
    COMMON_COLUMNS.opStatus,
    COMMON_COLUMNS.speed,
    COMMON_COLUMNS.description,
    COMMON_COLUMNS.classNames,
];

export const MONITORED_INTERFACE_COLUMNS = [
    COMMON_COLUMNS.name,
    MONITORED_SPECIFIC_COLUMNS.deviceName,
    MONITORED_SPECIFIC_COLUMNS.deviceIpAddress,
    MONITORED_SPECIFIC_COLUMNS.collector,
    COMMON_COLUMNS.pollingStatus,
    COMMON_COLUMNS.priority,
    COMMON_COLUMNS.type,
    COMMON_COLUMNS.adminStatus,
    COMMON_COLUMNS.opStatus,
    COMMON_COLUMNS.speed,
    COMMON_COLUMNS.description,
    COMMON_COLUMNS.classNames,
];
